import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import qz from 'qz-tray'
import { dispatchSnackbar } from '../helpers'
import { useCreateQzTraySignatureMutation } from 'services/devices'
import { DEFAULT_PDF_WIDTH } from 'utils/constants'

const useQzTray = () => {
  const dispatch = useDispatch()
  const [availablePrinters, setAvailablePrinters] = useState<string[]>([])
  const [createQzTraySignature] = useCreateQzTraySignatureMutation()
  const [isConnecting, setIsConnecting] = useState(false)

  // QZ Tray Security Setup, run on component mount
  useEffect(() => {
    qz.security.setCertificatePromise(async function () {
      // this file is hosted in the public folder
      const response = await fetch('/qz-tray-cert.txt', {
        cache: 'no-store',
        headers: { 'Content-Type': 'text/plain' },
      })
      return response.text()
    })

    qz.security.setSignatureAlgorithm('SHA512')
    qz.security.setSignaturePromise(async function (toSign) {
      const signResponse = await createQzTraySignature({ body: { toSign: toSign } }).unwrap()
      return signResponse.signature
    })

    // Cleanup on unmount
    return () => {
      if (qz.websocket.isActive()) {
        qz.websocket.disconnect()
      }
    }
  }, [])

  const testConnection = async () => {
    if (isConnecting) return
    setIsConnecting(true)

    try {
      if (qz.websocket.isActive()) {
        dispatchSnackbar(dispatch, 'success', `Connected to GreenSpark Print`)
      } else {
        await qz.websocket.connect()
        dispatchSnackbar(dispatch, 'success', `Connected to GreenSpark Print`)
      }
    } catch (error) {
      // Only show error when manually testing connection
      dispatchSnackbar(dispatch, 'error', `Failed to connect to print server. Is GreenSpark Print running?`)
    } finally {
      setIsConnecting(false)
    }
  }

  const qzConnect = async () => {
    if (isConnecting) return
    setIsConnecting(true)

    try {
      await qz.websocket.connect()
      dispatchSnackbar(dispatch, 'success', `Connected to GreenSpark Print`)
    } catch (error) {
      // Silently handle connection errors
    } finally {
      setIsConnecting(false)
    }
  }

  const qzDisconnect = async () => {
    await qz.websocket.disconnect()
  }

  const qzFindPrinters = async () => {
    try {
      if (!qz.websocket.isActive()) {
        await qzConnect()
      }

      const printers = await qz.printers.find()
      const availablePrinters = Array.isArray(printers) ? printers : []
      setAvailablePrinters(availablePrinters)
      await qzDisconnect()
      return availablePrinters
    } catch (error) {
      if (qz.websocket.isActive()) {
        dispatchSnackbar(dispatch, 'error', 'Failed to find printers')
      }
      return []
    }
  }

  const qzIsActive = () => {
    return qz.websocket.isActive()
  }

  const qzPrint = async (file, printer, numberOfCopiesOverride?: number) => {
    console.log('qzPrint called. Printer:', printer)
    try {
      if (!qz.websocket.isActive()) {
        await qz.websocket.connect()
      }

      const copies = numberOfCopiesOverride ?? parseInt(localStorage.getItem('numberOfCopies') ?? '1')

      const config = qz.configs.create(printer, {
        scaleContent: false,
        duplex: 'one-sided',
        copies,
      })

      const data = [{ type: 'pdf', format: 'base64', data: file }]

      await qz.print(config, data)
      dispatchSnackbar(dispatch, 'success', `Printed to ${printer}`)
      await qzDisconnect()
    } catch (error: any) {
      dispatchSnackbar(dispatch, 'info', `Printing with browser print dialog`)
      await qzDisconnect()
    }
  }

  const qzPrintResponsive = async (file, printer) => {
    console.log('qzPrintResponsive called. Printer:', printer)
    try {
      if (!qz.websocket.isActive()) {
        await qz.websocket.connect()
      }

      const paperSize = parseInt(localStorage.getItem('paperSize') ?? DEFAULT_PDF_WIDTH)
      const paperWidth = paperSize === 216 ? null : paperSize
      const paperHeight = paperSize === 216 ? null : 2000

      const config = qz.configs.create(printer, {
        duplex: 'one-sided',
        units: 'mm',
        scaleContent: true,
        size: {
          width: paperWidth,
          height: paperHeight,
        },
      })

      const data = [{ type: 'pdf', format: 'base64', data: file }]

      await qz.print(config, data)
      dispatchSnackbar(dispatch, 'success', `Printed to ${printer}`)
      await qzDisconnect()
    } catch (error: any) {
      dispatchSnackbar(dispatch, 'info', `Printing with browser print dialog`)
      await qzDisconnect()
    }
  }

  return {
    qzFindPrinters,
    qzPrint,
    qzPrintResponsive,
    qzConnect,
    qzIsActive,
    qzDisconnect,
    testConnection,
    availablePrinters, // Expose the printers state
  }
}

export default useQzTray
